import React, { useEffect, useContext, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";

import classes from "./Actions.module.css";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "./../../../../Component/UI/Table/Table";

import MUIIconButton from "./../../../../Component/UI/MUIIconButton/MUIIconButton";
import { AuthContext } from "../../../../context/auth-context";
import Spinner from "./../../../../Component/Spinner/Spinner";
import {
  deleteOrArchiveActionMetric,
  fetchActionMetrics,
  updateActionMetric,
} from "../../../../redux/actions/metricActions";
import { CommonInfoCard } from "./ActionModal";
import {
  Typography,
  Tooltip,
  ListItemText,
  ListItem,
  List,
  DialogContent,
  Dialog,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { getActionSources } from "../../../../services/apiService";
import { extractDomain } from "../../../../utils/helpers";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import MUIButton from "../../../../Component/UI/MUIButton/MUIButton";
import { toast, ToastContainer } from "react-toastify";
import FilterSelection from "../../common/FilterSelection";
import { prepareSelectOptions } from "./Actions";
import EmptyView from "../../../../Component/EmptyView/EmptyView";
import { Close } from "@mui/icons-material";

const TABLE_HEADERS = [
  { id: "checks", name: "", sorting: false },
  { id: "details", name: "Case", sorting: false },
  { id: "Actions", name: "Actions", sorting: true },
  { id: "llm_sources", name: "Training Data Sources", sorting: false },
  { id: "sources", name: "AI Generated Online Sources", sorting: false },
  { id: "actions", name: "Manage", sorting: false },
];

const ActionList = ({ selectedIds, setSelectedIds }) => {
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [filters, setFilters] = useState({
    brand: [],
    product: [],
    llm: [],
    market: [],
    geo: [],
    persona: [],
    category: [],
  });
  const dispatch = useDispatch();
  const actionMetrics = useSelector((state) => state.actionMetrics);
  const { user } = useContext(AuthContext);
  const customerId = user?.customer_id || 0;
  const [modalData, setModalData] = useState(null);

  const data = actionMetrics?.data?.actionList;
  const actionMetricsData = data?.filter((item) => item?.is_active);
  const isAdmin =
    user?.user_role?.role?.name == "Company admin" ||
    user?.user_role?.role?.name == "Super User";

  const handleCheckboxChange = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((itemId) => itemId !== id)
    );
  };

  useEffect(() => {
    if (actionMetricsData && actionMetricsData.length > 0) {
      const options = prepareSelectOptions(
        actionMetricsData?.map((item) => item?.action_json)
      );

      const defaultLlm = options.llm?.some((item) => item.value === "gpt-4o")
        ? ["gpt-4o"]
        : [options.llm?.[0]?.value || ""];

      const defaultGeo = options.geo?.some((item) => item.value === "USA")
        ? ["USA"]
        : [options.geo?.[0]?.value || ""];

      const defaultMarket = options.market?.some(
        (item) => item.value === "General Market"
      )
        ? ["General Market"]
        : [options.market?.[0]?.value || ""];

      const defaultPersona = options.persona?.some(
        (item) => item.value === "General Persona"
      )
        ? ["General Persona"]
        : [options.persona?.[0]?.value || ""];

      const defaultCategory = [options?.category?.[0]?.value || ""];

      setFilters({
        brand: [options?.brand?.[0]?.value || ""],
        product: [],
        llm: defaultLlm,
        market: defaultMarket,
        geo: defaultGeo,
        persona: defaultPersona,
        category: defaultCategory,
      });
    }
  }, [actionMetrics]);

  const applyFilters = (data) => {
    return data?.filter((data) => {
      const item = data?.action_json;
      const isBrandSelected = filters.brand.length > 0;
      const isProductSelected = filters.product.length > 0;

      // Check if the item is valid for the selected brand filter
      const isBrandValid =
        isBrandSelected &&
        !item.isProduct && // Ensure it's not a product
        filters.brand.includes(item?.brand);

      // Check if the item is valid for the selected product filter
      const isProductValid =
        isProductSelected &&
        item.isProduct && // Ensure it's a product
        filters.product.includes(item?.brand);

      // Handle other filters
      const isLlmValid = !filters.llm.length || filters.llm.includes(item?.llm);
      const isMarketValid =
        !filters.market.length || filters.market.includes(item?.market);
      const isGeoValid =
        !filters.geo.length || filters.geo.includes(item?.geography);
      const isCatValid =
        !filters.category.length || filters.category.includes(item?.category);
      const isPersonaValid =
        !filters.persona.length || filters.persona.includes(item?.persona);

      // If no brand and product filters are selected, show all items but respect other filters
      if (!isBrandSelected && !isProductSelected) {
        return isLlmValid && isMarketValid && isGeoValid && isPersonaValid;
      }

      // Return filtered data for brands and products with other filters
      return (
        (isBrandValid || isProductValid) &&
        isLlmValid &&
        isMarketValid &&
        isGeoValid &&
        isCatValid &&
        isPersonaValid
      );
    });
  };

  const optionsMapping = prepareSelectOptions(
    actionMetricsData?.map((item) => item?.action_json)
  );

  const updateAction = (data, id, sources) => {
    const payload = {
      action_json: data?.action_json,
      is_archived: data?.is_archived,
      is_active: data?.is_active,
      source_links: sources,
    };
    dispatch(updateActionMetric(customerId, id, payload)).then(() => {
      dispatch(fetchActionMetrics(customerId));
      setLoadingIndex(null);
    });
  };

  const generateSourcesAndUpdateAction = async (data, id, itemIndex) => {
    if (data?.action_json?.rationale) {
      setLoadingIndex(itemIndex);
      const sources = await getActionSources({
        score_rationale: data?.action_json?.rationale,
      });

      updateAction(data, id, sources?.finalUrls);
    }
  };

  const handleFilterChange = (key, selectedOptions) => {
    const values = selectedOptions.map((option) => option.value);
    setFilters((prevFilters) => ({ ...prevFilters, [key]: values }));
  };

  const sourceLinksList = (source_links) => {
    const groupedLinks =
      source_links?.filter(Boolean).reduce((acc, url) => {
        const domain = extractDomain(url);
        if (domain) {
          (acc[domain] ||= []).push(url);
        }
        return acc;
      }, {}) ?? {};

    if (Object.entries(groupedLinks) && Object.entries(groupedLinks).length) {
      return (
        <ul style={{ listStyleType: "none", padding: 0, marginLeft: "30px" }}>
          {Object.entries(groupedLinks)?.map(([domain, urls], index) => (
            <li key={index}>
              <Tooltip
                title={
                  <div>
                    {Array.from(new Set(urls)).map((url, subIndex) => (
                      <div key={subIndex} style={{ marginBottom: "4px" }}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "block",
                            color: "white",
                            textDecoration: "underline",
                            cursor: "pointer",
                            marginBottom: 4,
                          }}
                        >
                          {url}
                        </a>
                      </div>
                    ))}
                  </div>
                }
                placement="top"
                arrow
              >
                <Typography
                  variant="body2"
                  style={{ color: "#007aff", textDecoration: "underline" }}
                >
                  {domain}
                  <p style={{ marginTop: "-10px" }}></p>
                </Typography>
              </Tooltip>
            </li>
          ))}
        </ul>
      );
    } else {
      // toast.error("failed to get source, try again !");
      return null;
    }
  };

  const trainingDataSources = (data) => {
    return (
      data?.sources &&
      data?.sources.length > 0 && (
        <ul style={{ listStyleType: "none" }}>
          {data?.sources?.map((obj, subIndex) => (
            <li key={subIndex}>
              <Typography variant="body2">
                <Tooltip
                  title={
                    Array.isArray(obj?.fullUrl) && obj?.fullUrl?.length > 0 ? (
                      <div>
                        {obj.fullUrl.map((url, index) => (
                          <div key={subIndex} style={{ marginBottom: "4px" }}>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "block",
                                color: "white",
                                textDecoration: "underline",
                                cursor: "pointer",
                                marginBottom: 4,
                              }}
                            >
                              {url}
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p
                        // href={`https://${obj?.domain_url}`}
                        // target="_blank"
                        // rel="noopener noreferrer"
                        style={{
                          display: "block",
                          color: "white",
                          textDecoration: "underline",
                          cursor: "not-allowed",
                          marginBottom: 4,
                        }}
                        disabled
                      >
                        {obj?.domain_url || "No URL available"}
                      </p>
                    )
                  }
                  arrow
                >
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#007aff",
                      textDecoration: "underline",
                    }}
                  >
                    {obj?.domain_url || "No URL available"}
                  </span>
                </Tooltip>
              </Typography>
            </li>
          ))}
        </ul>
      )
    );
  };

  const renderRow = (item, itemIndex) => {
    const data = item?.action_json;
    const isChecked = selectedIds.includes(item.id);
    return (
      <StyledTableRow key={item.id}>
        <StyledTableCell align="left" style={{ width: 50 }}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
          />
        </StyledTableCell>
        <StyledTableCell align="left" style={{ width: 400 }}>
          <CommonInfoCard useCardWrapper={false} data={data} />
        </StyledTableCell>
        <StyledTableCell component="th" scope="row" style={{ width: 400 }}>
          <div className={classes.brand_product}>
            <span
              style={{ cursor: data?.compTitles?.length > 0 ? "pointer" : "" }}
              onClick={() =>
                data?.compTitles &&
                data?.compTitles.length > 0 &&
                setModalData({
                  actions: [
                    data?.title || data?.mainTitle,
                    ...data?.compTitles,
                  ],
                })
              }
            >
              {" "}
              {data?.title || data?.mainTitle}{" "}
            </span>
          </div>
          {/* <ul>
            {data?.compTitles && data?.compTitles.length > 0
              ? data?.compTitles?.map((item) => {
                  return <li> {item}</li>;
                })
              : null}
          </ul> */}
        </StyledTableCell>

        <StyledTableCell align="left" style={{ width: 150 }}>
          {trainingDataSources(data)}
        </StyledTableCell>

        <StyledTableCell align="left" style={{ width: 150 }}>
          {sourceLinksList(item?.source_links) !== null ? (
            sourceLinksList(item?.source_links)
          ) : loadingIndex === itemIndex ? (
            <Spinner />
          ) : item?.action_json?.rationale ? (
            <MUIButton
              onClick={() =>
                generateSourcesAndUpdateAction(item, item?.id, itemIndex)
              }
              variant="outlined"
              style={{ color: "#007aff", marginLeft: "30px" }}
              startIcon={<AutoAwesomeOutlinedIcon />}
              // disabled={!item?.action_json?.rationale}
            >
              Generate
            </MUIButton>
          ) : null}
        </StyledTableCell>

        {isAdmin && (
          <StyledTableCell align="left">
            <div>
              {/* <MUIIconButton
              style={{ color: "#6c757d" }}
              tooltip="Act"
              className={classes.action_btn}
              // onClick={() => handledeleteOrArchiveActionMetric(item.id, 0)}
            >
              <BuildOutlinedIcon />
            </MUIIconButton> */}

              <MUIIconButton
                style={{ color: "#6c757d" }}
                tooltip="Archive"
                className={classes.action_btn}
                onClick={() => handledeleteOrArchiveActionMetric(item.id, 1)}
              >
                <ArchiveOutlinedIcon />
              </MUIIconButton>

              <MUIIconButton
                style={{ color: "#da1a04" }}
                tooltip="Delete"
                className={classes.action_btn}
                onClick={() => handledeleteOrArchiveActionMetric(item.id, 0)}
              >
                <DeleteOutlineIcon />
              </MUIIconButton>
            </div>
          </StyledTableCell>
        )}
      </StyledTableRow>
    );
  };

  const handledeleteOrArchiveActionMetric = (actionId, isArchived) => {
    dispatch(
      deleteOrArchiveActionMetric(customerId, actionId, isArchived)
    ).then(() => {
      dispatch(fetchActionMetrics(customerId));
    });
  };
  return (
    <>
      {(actionMetrics.loading || actionMetrics.deleteLoading) && <Spinner />}

      {!actionMetrics.loading && !actionMetrics.deleteLoading ? (
        !actionMetricsData || actionMetricsData.length === 0 ? (
          <EmptyView message="Nothing here!" />
        ) : (
          <>
            <div className="row mt-4">
              {[
                "brand",
                "product",
                "category",
                "llm",
                "market",
                "geo",
                "persona",
              ].map((filterKey) => (
                <div
                  style={{ width: "210px" }}
                  className="col-md-1"
                  key={filterKey}
                >
                  <FilterSelection
                    options={optionsMapping[filterKey]}
                    type={filterKey}
                    selectedOptions={filters[filterKey].map((value) => ({
                      value,
                      label: value,
                    }))}
                    handleChange={(selectedOptions) =>
                      handleFilterChange(filterKey, selectedOptions)
                    }
                  />
                </div>
              ))}
            </div>
            <p></p>
            <Table
              headers={
                isAdmin
                  ? TABLE_HEADERS
                  : TABLE_HEADERS.filter((obj) => obj.name !== "Manage")
              }
              data={applyFilters(actionMetricsData)}
              render={renderRow}
            />
          </>
        )
      ) : null}

      <Dialog
        open={!!modalData}
        onClose={() => setModalData(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Action Data Full Summary</span>
          <IconButton onClick={() => setModalData(null)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: "12px" }}>
            {/* <p style={{ fontSize: "17px", fontWeight: "bold" }}>
              {modalData?.category}
            </p> */}
            <List>
              {modalData?.actions.map((action, j) => (
                <ListItem key={j} sx={{ padding: "2px 0" }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: "14px" }}
                    primary={`• ${action}`}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        </DialogContent>
      </Dialog>

      <ToastContainer />
    </>
  );
};

export default ActionList;
